(() => {
  if (window.__ftRelatedProductsInit) return;
  window.__ftRelatedProductsInit = true;

  const MAX_BESTSELLING_PAGES = 6;
  const bestSellingRankCache = (window.__ftBestSellingRankCache ||= new Map());

  const clamp = (num, min, max) => Math.min(max, Math.max(min, num));
  const safeParseInt = (value, fallback = 0) => {
    const n = Number.parseInt(String(value ?? ""), 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const fetchJson = async (url, { signal } = {}) => {
    const response = await fetch(url, { credentials: "same-origin", signal });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return response.json();
  };

  const fetchBestSellingRanks = async ({ allProductsUrl, handles, signal }) => {
    const normalizedHandles = Array.from(new Set((handles || []).filter(Boolean))).sort();
    if (!allProductsUrl || normalizedHandles.length === 0) return new Map();

    const cacheKey = `${allProductsUrl}::${normalizedHandles.join(",")}`;
    if (bestSellingRankCache.has(cacheKey)) return bestSellingRankCache.get(cacheKey);

    const promise = (async () => {
      const rankByHandle = new Map();
      const remaining = new Set(normalizedHandles);

      const base = new URL(allProductsUrl, window.location.origin);
      base.searchParams.set("sort_by", "best-selling");
      base.searchParams.set("view", "pdp-bestselling-handles");

      let page = 1;
      let pages = 1;

      while (page <= pages && page <= MAX_BESTSELLING_PAGES && remaining.size > 0) {
        if (signal?.aborted) break;

        const url = new URL(base.toString());
        url.searchParams.set("page", String(page));

        const data = await fetchJson(url.toString(), { signal }).catch(() => null);
        if (!data) break;

        const pageHandles = Array.isArray(data?.handles) ? data.handles : [];
        pages = safeParseInt(data?.pages, pages);

        pageHandles.forEach((handle, index) => {
          if (!remaining.has(handle)) return;
          const rank = (page - 1) * 50 + index + 1;
          rankByHandle.set(handle, rank);
          remaining.delete(handle);
        });

        page += 1;
      }

      return rankByHandle;
    })().catch(() => new Map());

    bestSellingRankCache.set(cacheKey, promise);
    return promise;
  };

  const buildGroupedSwatches = ({ groupItems, groupId, defaultHandle, visibleCount }) => {
    if (!Array.isArray(groupItems) || groupItems.length <= 1) return null;

    const count = clamp(safeParseInt(visibleCount, 4), 1, 50);
    const container = document.createElement("div");
    container.className = "collection-group-swatches";
    container.dataset.visibleCount = String(count);

    const fieldset = document.createElement("fieldset");
    fieldset.className = "collection-group-swatches__fieldset";

    const isSoldOut = (item) => item?.available === false;

    const name = `ft-related-grouped-swatch-${groupId}`;
    let ordered = groupItems.slice();
    const defaultIdx = ordered.findIndex((item) => item?.handle === defaultHandle);
    if (defaultIdx > 0) ordered.unshift(ordered.splice(defaultIdx, 1)[0]);

    // Keep the selected/default swatch first, but push sold-out swatches to the end.
    if (ordered.length > 1) {
      if (defaultIdx === -1) {
        const availableFirst = ordered.filter((item) => !isSoldOut(item));
        const soldOutLast = ordered.filter((item) => isSoldOut(item));
        ordered = [...availableFirst, ...soldOutLast];
      } else {
        const [first, ...rest] = ordered;
        const availableFirst = rest.filter((item) => !isSoldOut(item));
        const soldOutLast = rest.filter((item) => isSoldOut(item));
        ordered = [first, ...availableFirst, ...soldOutLast];
      }
    }

    ordered.forEach((item) => {
      const input = document.createElement("input");
      input.type = "radio";
      input.className = "sr-only";
      input.name = name;
      input.id = `${name}-${item.id}`;
      input.value = String(item.id);
      input.checked = item.handle === defaultHandle;
      input.disabled = item.available === false;
      input.dataset.ftGroupedHandle = item.handle || "";

      const label = document.createElement("label");
      label.className = "color-swatch";
      label.htmlFor = input.id;
      if (item.swatchImage) label.style.setProperty("--swatch-background", `url("${item.swatchImage}")`);

      const sr = document.createElement("span");
      sr.className = "sr-only";
      sr.textContent = item.title || "Color";
      label.appendChild(sr);

      fieldset.appendChild(input);
      fieldset.appendChild(label);
    });

    const viewport = document.createElement("div");
    viewport.className = "collection-group-swatches__viewport";
    viewport.appendChild(fieldset);

    const needsCarousel = groupItems.length > count;
    if (needsCarousel) {
      container.dataset.carousel = "true";

      const prev = document.createElement("button");
      prev.type = "button";
      prev.className = "collection-group-swatches__nav";
      prev.setAttribute("aria-label", "Previous colors");
      prev.setAttribute("data-swatch-nav", "prev");
      prev.innerHTML = "&lsaquo;";

      const next = document.createElement("button");
      next.type = "button";
      next.className = "collection-group-swatches__nav";
      next.setAttribute("aria-label", "Next colors");
      next.setAttribute("data-swatch-nav", "next");
      next.innerHTML = "&rsaquo;";

      container.appendChild(prev);
      container.appendChild(viewport);
      container.appendChild(next);
    } else {
      container.appendChild(viewport);
    }

    return container;
  };

  const initGroupedSwatchCarousel = (container) => {
    if (!container || container.getAttribute("data-carousel-init") === "true") return;
    container.setAttribute("data-carousel-init", "true");

    const viewport = container.querySelector(".collection-group-swatches__viewport");
    const fieldset = container.querySelector(".collection-group-swatches__fieldset");
    if (!viewport || !fieldset) return;

    const visibleCount = clamp(safeParseInt(container.dataset.visibleCount, 4), 1, 50);
    const prevBtn = container.querySelector('[data-swatch-nav="prev"]');
    const nextBtn = container.querySelector('[data-swatch-nav="next"]');

    const updateNavState = () => {
      if (!prevBtn || !nextBtn) return;
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      prevBtn.disabled = viewport.scrollLeft <= 1;
      nextBtn.disabled = viewport.scrollLeft >= maxScroll - 1;
    };

    const measureAndSetWidth = () => {
      if (!container.dataset.carousel) return;
      const sample = fieldset.querySelector("label.color-swatch");
      if (!sample) return;
      const swatchWidth = sample.getBoundingClientRect().width;
      const styles = getComputedStyle(fieldset);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || "0") || 0;
      const width = swatchWidth * visibleCount + gap * (visibleCount - 1);
      viewport.style.width = `${Math.ceil(width)}px`;
      updateNavState();
    };

    const scrollBy = (direction) => {
      const amount = viewport.clientWidth * 0.9 * direction;
      viewport.scrollBy({ left: amount, behavior: "smooth" });
    };

    if (prevBtn) prevBtn.addEventListener("click", () => scrollBy(-1));
    if (nextBtn) nextBtn.addEventListener("click", () => scrollBy(1));

    viewport.style.overflowX = container.dataset.carousel ? "auto" : "visible";
    viewport.style.overflowY = container.dataset.carousel ? "hidden" : "visible";
    viewport.style.scrollBehavior = "smooth";
    viewport.addEventListener("scroll", updateNavState, { passive: true });

    requestAnimationFrame(measureAndSetWidth);
    window.addEventListener("resize", measureAndSetWidth, { passive: true });
    updateNavState();
  };

  const ensureProductCardsVisible = (root) => {
    if (!root) return;
    root.querySelectorAll('product-card[reveal-on-scroll="true"]').forEach((card) => {
      card.style.opacity = "1";
      card.style.transform = "none";
      card.removeAttribute("reveal-on-scroll");
    });
  };

  const applyGrouping = ({ container, enabled, swatchMax }) => {
    if (!enabled || !container) return;
    if (container.getAttribute("data-ft-grouping-init") === "true") return;
    container.setAttribute("data-ft-grouping-init", "true");

    const wrappers = Array.from(container.querySelectorAll('[data-ft-rec-item="true"]'));
    const groupMap = new Map(); // coreKey => [wrappers]

    wrappers.forEach((wrapper) => {
      const groupable = wrapper.getAttribute("data-groupable") === "true";
      const coreKey = wrapper.getAttribute("data-core-key") || "";
      if (!groupable || !coreKey) return;
      if (!groupMap.has(coreKey)) groupMap.set(coreKey, []);
      groupMap.get(coreKey).push(wrapper);
    });

    groupMap.forEach((groupWrappers, coreKey) => {
      if (!Array.isArray(groupWrappers) || groupWrappers.length <= 1) return;

      const parsedItems = groupWrappers
        .map((wrapper) => ({
          wrapper,
          id: wrapper.getAttribute("data-product-id") || "",
          handle: wrapper.getAttribute("data-product-handle") || "",
          title: wrapper.getAttribute("data-product-title") || "",
          available: wrapper.getAttribute("data-product-available") !== "false",
          swatchImage: wrapper.getAttribute("data-product-swatch") || ""
        }))
        .filter((item) => item.handle);

      if (parsedItems.length <= 1) return;

      // Hide all but the first (active) wrapper.
      parsedItems.forEach((item, index) => {
        if (index === 0) return;
        item.wrapper.hidden = true;
        item.wrapper.dataset.ftGroupedAlternate = "true";
      });

      const active = parsedItems[0];
      const groupId = `${container.id || "container"}-${coreKey}`;
      const swatches = buildGroupedSwatches({
        groupItems: parsedItems,
        groupId,
        defaultHandle: active.handle,
        visibleCount: swatchMax
      });
      if (!swatches) return;

      swatches.dataset.coreKey = coreKey;

      const mount = active.wrapper.querySelector("[data-ft-grouping-mount]") || active.wrapper;
      mount.appendChild(swatches);
      initGroupedSwatchCarousel(swatches);

      swatches.addEventListener("change", (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (input.type !== "radio" || !input.dataset.ftGroupedHandle) return;

        const nextHandle = input.dataset.ftGroupedHandle;
        const currentWrapper = swatches.closest('[data-ft-rec-item="true"]');
        const nextItem = parsedItems.find((item) => item.handle === nextHandle);
        if (!currentWrapper || !nextItem || nextItem.wrapper === currentWrapper) return;

        const pageHidden = currentWrapper.dataset.ftPageHidden;

        currentWrapper.hidden = true;
        currentWrapper.dataset.ftGroupedAlternate = "true";
        delete currentWrapper.dataset.ftPageHidden;

        nextItem.wrapper.hidden = false;
        nextItem.wrapper.dataset.ftGroupedAlternate = "false";
        if (pageHidden === "true") nextItem.wrapper.dataset.ftPageHidden = "true";

        const nextMount = nextItem.wrapper.querySelector("[data-ft-grouping-mount]") || nextItem.wrapper;
        nextMount.appendChild(swatches);

        ensureProductCardsVisible(nextItem.wrapper);
      });
    });
  };

  const applyDrawerPaging = ({ drawerGrid, loadMoreButton, pageSize }) => {
    if (!drawerGrid) return;
    const size = clamp(safeParseInt(pageSize, 25), 1, 100);

    const getVisibleEntries = () =>
      Array.from(drawerGrid.querySelectorAll('[data-ft-rec-item="true"]')).filter((wrapper) => wrapper.hidden !== true);

    const update = () => {
      const entries = getVisibleEntries();
      entries.forEach((wrapper, index) => {
        if (index < size) {
          delete wrapper.dataset.ftPageHidden;
        } else {
          wrapper.dataset.ftPageHidden = "true";
        }
      });

      const anyHidden = entries.length > size;
      if (loadMoreButton) loadMoreButton.hidden = !anyHidden;
    };

    const revealNext = () => {
      const entries = getVisibleEntries();
      const currentlyVisible = entries.filter((w) => w.dataset.ftPageHidden !== "true").length;
      const nextVisible = Math.min(entries.length, currentlyVisible + size);

      entries.forEach((wrapper, index) => {
        if (index < nextVisible) {
          delete wrapper.dataset.ftPageHidden;
        } else {
          wrapper.dataset.ftPageHidden = "true";
        }
      });

      if (loadMoreButton) loadMoreButton.hidden = nextVisible >= entries.length;
    };

    update();
    if (loadMoreButton) loadMoreButton.addEventListener("click", revealNext);
  };

  const sortAndRender = async ({ root }) => {
    const allProductsUrl = root.getAttribute("data-all-products-url") || "";
    const gridCount = safeParseInt(root.getAttribute("data-grid-count"), 10);

    const gridList = root.querySelector("[data-ft-related-grid]");
    const drawerGrid = root.querySelector("[data-ft-related-drawer-grid]");
    if (!gridList || !drawerGrid) return;

    const allWrappers = Array.from(root.querySelectorAll('[data-ft-rec-item="true"]'));
    if (allWrappers.length === 0) return;

    const wrappers = allWrappers.slice();
    const handles = wrappers.map((w) => w.getAttribute("data-product-handle")).filter(Boolean);

    const byPriceThenTitle = (a, b) => {
      const aPrice = safeParseInt(a.getAttribute("data-product-price"), 0);
      const bPrice = safeParseInt(b.getAttribute("data-product-price"), 0);
      if (aPrice !== bPrice) return aPrice - bPrice;
      const aTitle = (a.getAttribute("data-product-title") || "").toLowerCase();
      const bTitle = (b.getAttribute("data-product-title") || "").toLowerCase();
      return aTitle.localeCompare(bTitle);
    };

    const render = (ordered) => {
      const fragmentGrid = document.createDocumentFragment();
      const fragmentDrawer = document.createDocumentFragment();

      ordered.forEach((wrapper, index) => {
        if (index < gridCount) fragmentGrid.appendChild(wrapper);
        else fragmentDrawer.appendChild(wrapper);
      });

      gridList.replaceChildren(fragmentGrid);
      drawerGrid.replaceChildren(fragmentDrawer);

      // Some cards may be left invisible if a reveal-on-scroll observer doesn't
      // re-register after we move nodes between containers.
      ensureProductCardsVisible(gridList);
      ensureProductCardsVisible(drawerGrid);
    };

    // Render immediately using the server order so the drawer is populated right away,
    // then upgrade ordering to best-selling once ranks are available.
    render(wrappers);

    if (!allProductsUrl || handles.length <= 1) return;

    const rankByHandle = await fetchBestSellingRanks({ allProductsUrl, handles }).catch(() => new Map());
    if (!(rankByHandle instanceof Map) || rankByHandle.size === 0) return;

    const byRankThenPrice = (a, b) => {
      const aHandle = a.getAttribute("data-product-handle") || "";
      const bHandle = b.getAttribute("data-product-handle") || "";
      const aRank = rankByHandle.get(aHandle);
      const bRank = rankByHandle.get(bHandle);

      if (typeof aRank === "number" && typeof bRank === "number" && aRank !== bRank) return aRank - bRank;
      if (typeof aRank === "number" && typeof bRank !== "number") return -1;
      if (typeof aRank !== "number" && typeof bRank === "number") return 1;

      return byPriceThenTitle(a, b);
    };

    const upgraded = wrappers.slice().sort(byRankThenPrice);
    render(upgraded);
  };

  const initSection = async (root) => {
    if (!root || root.getAttribute("data-ft-related-root") !== "true") return;
    if (root.getAttribute("data-ft-related-ready") === "true") return;

    const hasAnyItems = root.querySelector('[data-ft-rec-item="true"]') != null;
    if (!hasAnyItems) {
      if (root.__ftRelatedWaitObserver) return;

      const observer = new MutationObserver(() => {
        if (root.querySelector('[data-ft-rec-item="true"]')) {
          observer.disconnect();
          root.__ftRelatedWaitObserver = null;
          initSection(root);
        }
      });

      observer.observe(root, { childList: true, subtree: true });
      root.__ftRelatedWaitObserver = observer;
      return;
    }

    if (root.getAttribute("data-ft-related-init") === "true") return;
    root.setAttribute("data-ft-related-init", "true");

    await sortAndRender({ root });

    const gridList = root.querySelector("[data-ft-related-grid]");
    const drawerGrid = root.querySelector("[data-ft-related-drawer-grid]");
    const loadMoreButton = root.querySelector("[data-ft-related-load-more]");
    const pageSize = safeParseInt(drawerGrid?.getAttribute("data-page-size"), 25);

    const groupGridEnabled = root.getAttribute("data-group-grid-enabled") === "true";
    const groupDrawerEnabled = root.getAttribute("data-group-drawer-enabled") === "true";
    const swatchMax = safeParseInt(root.getAttribute("data-group-swatch-max"), 4);

    applyGrouping({ container: gridList, enabled: groupGridEnabled, swatchMax });
    applyGrouping({ container: drawerGrid, enabled: groupDrawerEnabled, swatchMax });

    applyDrawerPaging({ drawerGrid, loadMoreButton, pageSize });

    // Hide the drawer trigger if there are no drawer items.
    const drawerTrigger = root.querySelector("[data-ft-related-drawer-trigger]");
    if (drawerTrigger && drawerGrid) {
      const hasAny = drawerGrid.querySelector('[data-ft-rec-item="true"]') != null;
      drawerTrigger.hidden = !hasAny;
    }

    // Hide load-more if drawer has <= pageSize items.
    if (loadMoreButton && drawerGrid) {
      const visibleEntries = Array.from(drawerGrid.querySelectorAll('[data-ft-rec-item="true"]')).filter((w) => w.hidden !== true);
      loadMoreButton.hidden = visibleEntries.length <= pageSize;
    }

    root.setAttribute("data-ft-related-ready", "true");
  };

  const scan = () => {
    document.querySelectorAll('[data-ft-related-root="true"]').forEach((root) => initSection(root));
  };

  const observe = () => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.('[data-ft-related-root="true"]')) initSection(node);
          node.querySelectorAll?.('[data-ft-related-root="true"]').forEach((root) => initSection(root));
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      scan();
      observe();
    });
  } else {
    scan();
    observe();
  }
})();
